import type { CommuteDestination, CommuteTime, TravelMode } from './types';

function getTravelMode(mode: TravelMode): google.maps.TravelMode {
  const map: Record<TravelMode, google.maps.TravelMode> = {
    DRIVING: google.maps.TravelMode.DRIVING,
    WALKING: google.maps.TravelMode.WALKING,
    TRANSIT: google.maps.TravelMode.TRANSIT,
    BICYCLING: google.maps.TravelMode.BICYCLING,
  };
  return map[mode];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return '< 1 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} hr`;
  return `${hours} hr ${remainingMinutes} min`;
}

function formatDistance(meters: number): string {
  const miles = meters / 1609.34;
  if (miles < 0.1) return '< 0.1 mi';
  return `${miles.toFixed(1)} mi`;
}

async function calculateSingleRoute(
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
  mode: TravelMode,
): Promise<CommuteTime | null> {
  if (typeof google === 'undefined') return null;

  const directionsService = new google.maps.DirectionsService();

  return new Promise((resolve) => {
    const request: google.maps.DirectionsRequest = {
      origin,
      destination: { lat: destination.lat, lng: destination.lng },
      travelMode: getTravelMode(mode),
      ...(mode === 'TRANSIT' ? { transitOptions: { departureTime: new Date() } } : {}),
    };

    directionsService.route(request, (result, status) => {
      if (status !== google.maps.DirectionsStatus.OK || !result?.routes[0]?.legs[0]) {
        resolve(null);
        return;
      }

      const leg = result.routes[0].legs[0];
      resolve({
        mode,
        duration: leg.duration?.value ?? 0,
        distance: leg.distance?.value ?? 0,
        durationText: formatDuration(leg.duration?.value ?? 0),
        distanceText: formatDistance(leg.distance?.value ?? 0),
      });
    });
  });
}

const ALL_MODES: Array<TravelMode> = ['DRIVING', 'WALKING', 'TRANSIT', 'BICYCLING'];

export async function calculateCommuteTimes(
  origin: { lat: number; lng: number },
  destination: CommuteDestination,
  modes: Array<TravelMode> = ALL_MODES,
): Promise<Array<CommuteTime>> {
  const results = await Promise.allSettled(modes.map((mode) => calculateSingleRoute(origin, destination, mode)));

  return results
    .map((result) => (result.status === 'fulfilled' ? result.value : null))
    .filter((time): time is CommuteTime => time !== null);
}
